/**
 * WordPress dependencies
 */
import { store, getContext, getElement } from "@wordpress/interactivity";

/**
 * Captura los parámetros UTM de la URL y los guarda en cookies
 */
function captureAndStoreUtmParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const utmParams = ["utm_source", "utm_medium", "utm_campaign"];

    utmParams.forEach((param) => {
        const value = urlParams.get(param);
        if (value) {
            // Guardar en cookie por 30 días
            const expires = new Date();
            expires.setDate(expires.getDate() + 30);
            document.cookie = `${param}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
        }
    });
}

/**
 * Obtiene el valor de una cookie por nombre
 */
function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        return decodeURIComponent(parts.pop().split(";").shift());
    }
    return "";
}

/**
 * Obtiene los parámetros UTM de cookies o URL
 */
function getUtmParams() {
    const urlParams = new URLSearchParams(window.location.search);
    return {
        utm_source: urlParams.get("utm_source") || getCookie("utm_source") || "",
        utm_medium: urlParams.get("utm_medium") || getCookie("utm_medium") || "",
        utm_campaign: urlParams.get("utm_campaign") || getCookie("utm_campaign") || "",
    };
}

// Capturar UTMs al cargar el script
captureAndStoreUtmParams();

const { state } = store("fct", {
    state: {
        firstName: "",
        lastName: "",
        email: "",
        department: "",
        errors: {
            firstName: "",
            lastName: "",
            email: "",
        },
        isSubmitting: false,
        isSuccess: false,
        successMessage: "",
    },
    actions: {
        /**
         * Valida que el campo solo contenga letras y espacios
         */
        validateFirstName() {
            const element = getElement();
            const value = element.ref.value.trim();

            state.firstName = value;

            // Campo requerido
            if (!value) {
                state.errors.firstName = "El nombre es obligatorio.";
                element.ref.setAttribute("aria-invalid", "true");
                return false;
            }

            // Máximo 100 caracteres
            if (value.length > 100) {
                state.errors.firstName = "El nombre no puede exceder 100 caracteres.";
                element.ref.setAttribute("aria-invalid", "true");
                return false;
            }

            // Solo letras y espacios
            const nameRegex = /^[a-zA-ZÀ-ÿ\u00f1\u00d1\s]+$/;
            if (!nameRegex.test(value)) {
                state.errors.firstName = "El nombre solo puede contener letras.";
                element.ref.setAttribute("aria-invalid", "true");
                return false;
            }

            // Todo válido
            state.errors.firstName = "";
            element.ref.setAttribute("aria-invalid", "false");
            return true;
        },

        /**
         * Valida el apellido con las mismas reglas que el nombre
         */
        validateLastName() {
            const element = getElement();
            const value = element.ref.value.trim();

            state.lastName = value;

            // Campo requerido
            if (!value) {
                state.errors.lastName = "El apellido es obligatorio.";
                element.ref.setAttribute("aria-invalid", "true");
                return false;
            }

            // Máximo 100 caracteres
            if (value.length > 100) {
                state.errors.lastName = "El apellido no puede exceder 100 caracteres.";
                element.ref.setAttribute("aria-invalid", "true");
                return false;
            }

            // Solo letras y espacios
            const nameRegex = /^[a-zA-ZÀ-ÿ\u00f1\u00d1\s]+$/;
            if (!nameRegex.test(value)) {
                state.errors.lastName = "El apellido solo puede contener letras.";
                element.ref.setAttribute("aria-invalid", "true");
                return false;
            }

            // Todo válido
            state.errors.lastName = "";
            element.ref.setAttribute("aria-invalid", "false");
            return true;
        },

        /**
         * Valida el email
         */
        validateEmail() {
            const element = getElement();
            const value = element.ref.value.trim();

            state.email = value;

            // Campo requerido
            if (!value) {
                state.errors.email = "El email es obligatorio.";
                element.ref.setAttribute("aria-invalid", "true");
                return false;
            }

            // Máximo 100 caracteres
            if (value.length > 100) {
                state.errors.email = "El email no puede exceder 100 caracteres.";
                element.ref.setAttribute("aria-invalid", "true");
                return false;
            }

            // No debe contener espacios
            if (/\s/.test(value)) {
                state.errors.email = "El email no puede contener espacios.";
                element.ref.setAttribute("aria-invalid", "true");
                return false;
            }

            // Validación de formato de email
            const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (!emailRegex.test(value)) {
                state.errors.email = "Por favor, ingresa un email válido.";
                element.ref.setAttribute("aria-invalid", "true");
                return false;
            }

            // Todo válido
            state.errors.email = "";
            element.ref.setAttribute("aria-invalid", "false");
            return true;
        },

        /**
         * Maneja el envío del formulario
         */
        async handleSubmit(event) {
            event.preventDefault();

            const context = getContext();
            const form = event.target;

            // Obtener valores del formulario
            const formData = new FormData(form);
            state.firstName = formData.get("first_name").trim();
            state.lastName = formData.get("last_name").trim();
            state.email = formData.get("email").trim();
            state.department = formData.get("department");

            // Validar todos los campos
            const firstNameValid = state.firstName && /^[a-zA-ZÀ-ÿ\u00f1\u00d1\s]+$/.test(state.firstName) && state.firstName.length <= 100;
            const lastNameValid = state.lastName && /^[a-zA-ZÀ-ÿ\u00f1\u00d1\s]+$/.test(state.lastName) && state.lastName.length <= 100;
            const emailValid = state.email && /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(state.email) && !/\s/.test(state.email) && state.email.length <= 100;

            // Actualizar errores
            if (!firstNameValid) {
                if (!state.firstName) {
                    state.errors.firstName = "El nombre es obligatorio.";
                } else if (state.firstName.length > 100) {
                    state.errors.firstName = "El nombre no puede exceder 100 caracteres.";
                } else {
                    state.errors.firstName = "El nombre solo puede contener letras.";
                }
            } else {
                state.errors.firstName = "";
            }

            if (!lastNameValid) {
                if (!state.lastName) {
                    state.errors.lastName = "El apellido es obligatorio.";
                } else if (state.lastName.length > 100) {
                    state.errors.lastName = "El apellido no puede exceder 100 caracteres.";
                } else {
                    state.errors.lastName = "El apellido solo puede contener letras.";
                }
            } else {
                state.errors.lastName = "";
            }

            if (!emailValid) {
                if (!state.email) {
                    state.errors.email = "El email es obligatorio.";
                } else if (state.email.length > 100) {
                    state.errors.email = "El email no puede exceder 100 caracteres.";
                } else if (/\s/.test(state.email)) {
                    state.errors.email = "El email no puede contener espacios.";
                } else {
                    state.errors.email = "Por favor, ingresa un email válido.";
                }
            } else {
                state.errors.email = "";
            }

            // Si hay errores, detener el envío
            if (!firstNameValid || !lastNameValid || !emailValid) {
                return;
            }

            // Iniciar proceso de envío
            state.isSubmitting = true;
            state.isSuccess = false;

            try {
                // Preparar datos para enviar al servidor
                const ajaxData = new FormData();
                ajaxData.append("action", "fct_download_form_submit");
                ajaxData.append("nonce", context.nonce);
                ajaxData.append("name", state.firstName);
                ajaxData.append("lastname", state.lastName);
                ajaxData.append("email", state.email);
                ajaxData.append("job_department", state.department);
                ajaxData.append("formName", context.formName);
                ajaxData.append("pageType", context.pageType);
                ajaxData.append("downloadName", context.downloadName);
                ajaxData.append("resource_id", context.resourceId);

                // Agregar parámetros UTM
                const utmParams = getUtmParams();
                ajaxData.append("utm_source", utmParams.utm_source);
                ajaxData.append("utm_medium", utmParams.utm_medium);
                ajaxData.append("utm_campaign", utmParams.utm_campaign);

                const response = await fetch(context.ajaxUrl, {
                    method: "POST",
                    body: ajaxData,
                });

                const result = await response.json();

                if (!result.success) {
                    throw new Error(result.data?.message || "Error al enviar el formulario.");
                }

                // Mostrar mensaje de éxito
                state.isSuccess = false;
                state.successMessage = context.successMessage;
                // Redirigir si hay URL configurada
                if (context.redirectUrl) {
                    window.location.href = context.redirectUrl;
                }

                // Resetear formulario después de 3 segundos
                setTimeout(() => {
                    state.firstName = "";
                    state.lastName = "";
                    state.email = "";
                    state.department = "";
                    state.isSuccess = false;
                    form.reset();
                }, 3000);
            } catch (error) {
                console.error("Error al enviar el formulario:", error);
                state.errors.email = error.message || "Error al enviar el formulario.";
            } finally {
                state.isSubmitting = false;
            }
        },
    },
});
